import { useCallback, useEffect, useState } from 'react';
import {
  Box, Button, Flex, IconButton, Menu, MenuButton, MenuItem, MenuList, Spinner, Text,
  useDisclosure, useToast,
} from '@chakra-ui/react';
import { AddIcon, ChevronRightIcon, CloseIcon, DeleteIcon, WarningIcon } from '@chakra-ui/icons';
import { LuArchive, LuCheck, LuCrosshair, LuRotateCcw } from 'react-icons/lu';
import { Trans, useTranslation } from 'react-i18next';
import { createGoal, getReferenceData, setGoalAccounts, updateGoal } from '../../api/endpointsBackend';
import {
  STATUS_COLORS, durationWords, formatAmount, formatMonthYear, goalStatusKey, hexToTint,
  monthsUntil,
} from '../../utils/goals';
import MovementDetailPanel from '../movements/MovementDetailPanel';
import Figure from '../shared/Figure';
import GoalAccountsPicker from './GoalAccountsPicker';
import GoalAttachModal from './GoalAttachModal';
import GoalMonthDots from './GoalMonthDots';
import GoalMovementsList from './GoalMovementsList';
import RateSourceTooltip from '../shared/RateSourceTooltip';
import GoalForm from './GoalForm';
import GoalIcon from './GoalIcon';
import { GoalBar } from './GoalProgress';

function SectionTitle({ children, action }) {
  return (
    <Flex align="center" justify="space-between" mb={1}>
      <Text textStyle="microLabel">{children}</Text>
      {action}
    </Flex>
  );
}

function SummaryRow({ label, value, onEdit }) {
  return (
    <Flex
      align="center"
      justify="space-between"
      h="36px"
      borderBottom="1px solid"
      borderColor="redesign.border"
      cursor={onEdit ? 'pointer' : 'default'}
      _hover={onEdit ? { bg: 'redesign.bg.hover' } : undefined}
      onClick={onEdit}
    >
      <Text fontSize="sm" color="redesign.text.muted">{label}</Text>
      <Flex align="center" gap={2}>
        <Text fontSize="sm" color="redesign.heading">{value}</Text>
        {onEdit && <ChevronRightIcon color="redesign.text.faint" boxSize={4} />}
      </Flex>
    </Flex>
  );
}

function toPayload(values) {
  const isTargetDate = values.saving_mode === 'target_date';
  return {
    name: values.name.trim(),
    color: values.color || null,
    icon: values.icon || null,
    target_amount: parseFloat(values.target_amount),
    currency: values.currency,
    start_date: values.start_date,
    saving_mode: values.saving_mode,
    target_date: isTargetDate ? values.target_date : null,
    monthly_contribution: isTargetDate ? null : parseFloat(values.monthly_contribution),
  };
}

function Insight({ goal, summary }) {
  const { t } = useTranslation('goals');
  const amount = (n) => `${summary.currency} ${formatAmount(n)}`;
  let content;
  if (summary.is_achieved) content = t('insight.achieved');
  else if (summary.is_overdue) content = t('insight.overdue');
  else if (goal.saving_mode === 'target_date' && summary.required_monthly > 0) {
    content = <Trans ns="goals" i18nKey="insight.requiredMonthly" values={{ amount: amount(summary.required_monthly) }} components={{ strong: <strong /> }} />;
  } else if (summary.projected_end) {
    // "On pace" is a claim, so it is only made when the goal is on pace.
    content = (
      <Trans
        ns="goals"
        i18nKey={summary.is_off_track ? 'insight.projectedEndBehind' : 'insight.projectedEnd'}
        values={{ duration: durationWords(summary.projected_end), date: formatMonthYear(summary.projected_end) }}
        components={{ strong: <strong /> }}
      />
    );
  }
  if (!content) return null;
  return (
    <Flex align="center" justify="center" gap={2} py={3}>
      <Box as={LuCrosshair} boxSize="14px" color="redesign.accent.blue" flexShrink={0} />
      <Text fontSize="sm" color="redesign.heading">{content}</Text>
    </Flex>
  );
}

/**
 * One goal: where it stands, where its money sits, and the movements that got
 * it there. Doubles as the create form when no goal is selected.
 */
export default function GoalDetailPanel({
  goalId, row, draft, loading, onClose, onChanged, onDelete, onStatusChange, canUpdate,
}) {
  const { t } = useTranslation('goals');
  const toast = useToast();
  const isCreateMode = !goalId;
  const [isEditing, setIsEditing] = useState(false);
  const [editingAccounts, setEditingAccounts] = useState(false);
  const [accountIds, setAccountIds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [reference, setReference] = useState({ accounts: [], categories: [], tags: [] });
  const [selectedMovementId, setSelectedMovementId] = useState(null);
  const [overlayDirty, setOverlayDirty] = useState(false);
  const [movementsRefreshKey, setMovementsRefreshKey] = useState(0);
  const { isOpen: isAttachOpen, onOpen: onAttachOpen, onClose: onAttachClose } = useDisclosure();

  useEffect(() => {
    getReferenceData().then(setReference).catch(() => {});
  }, []);

  useEffect(() => {
    setIsEditing(false);
    setEditingAccounts(false);
    setSelectedMovementId(null);
    setOverlayDirty(false);
  }, [goalId]);

  const handleCloseOverlay = useCallback(() => {
    setSelectedMovementId(null);
    if (overlayDirty) {
      setMovementsRefreshKey((k) => k + 1);
      setOverlayDirty(false);
      onChanged();
    }
  }, [overlayDirty, onChanged]);

  const showError = (err) => {
    const detail = err.response?.data?.detail;
    toast({
      title: t('toasts.errorTitle'),
      description: typeof detail === 'string' ? detail : t('toasts.saveFailed'),
      status: 'error',
      duration: 5000,
      isClosable: true,
    });
  };

  const handleSubmit = async (values) => {
    setSaving(true);
    try {
      if (isCreateMode) {
        const created = await createGoal({ ...toPayload(values), account_ids: values.account_ids });
        toast({ title: t('toasts.successTitle'), description: t('toasts.created', { name: created.name }), status: 'success', duration: 3000, isClosable: true });
        onChanged(created.id);
      } else {
        const updated = await updateGoal(goalId, toPayload(values));
        toast({ title: t('toasts.successTitle'), description: t('toasts.updated', { name: updated.name }), status: 'success', duration: 3000, isClosable: true });
        setIsEditing(false);
        onChanged();
      }
    } catch (err) {
      showError(err);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAccounts = async () => {
    setSaving(true);
    try {
      await setGoalAccounts(goalId, accountIds);
      setEditingAccounts(false);
      onChanged();
    } catch (err) {
      showError(err);
    } finally {
      setSaving(false);
    }
  };

  const header = (
    <Flex justify="space-between" align="center" px={{ base: 2, md: 4 }} py={{ base: 2, md: 3 }} borderBottom="1px solid" borderColor="redesign.border" flexShrink={0}>
      <Text fontSize="sm" fontWeight="500" color="redesign.text">
        {isCreateMode ? t('detail.headerNew') : isEditing ? t('detail.headerEdit') : t('detail.header')}
      </Text>
      <Flex gap={2} align="center">
        {!isCreateMode && row && !isEditing && canUpdate && (
          <Button size="xs" variant="outline" onClick={() => setIsEditing(true)}>{t('detail.edit')}</Button>
        )}
        {!isCreateMode && row && (canUpdate || onDelete) && (
          <Menu>
            <MenuButton as={IconButton} icon={<Text fontSize="lg">⋯</Text>} variant="ghost" size="sm" aria-label={t('detail.options')} />
            <MenuList>
              {canUpdate && row.status !== 'ready' && (
                <MenuItem icon={<LuCheck />} onClick={() => onStatusChange(row, 'ready')}>{t('actions.markReady')}</MenuItem>
              )}
              {canUpdate && row.status !== 'active' && (
                <MenuItem icon={<LuRotateCcw />} onClick={() => onStatusChange(row, 'active')}>{t('actions.reactivate')}</MenuItem>
              )}
              {canUpdate && row.status !== 'archived' && (
                <MenuItem icon={<LuArchive />} onClick={() => onStatusChange(row, 'archived')}>{t('actions.archive')}</MenuItem>
              )}
              {onDelete && (
                <MenuItem icon={<DeleteIcon />} color="redesign.accent.red" onClick={() => onDelete(row)}>{t('actions.delete')}</MenuItem>
              )}
            </MenuList>
          </Menu>
        )}
        <IconButton icon={<CloseIcon />} size="sm" variant="ghost" onClick={onClose} aria-label={t('detail.closeAria')} />
      </Flex>
    </Flex>
  );

  let body;
  if (isCreateMode || isEditing) {
    body = (
      <GoalForm
        key={goalId || draft?.key || 'new'}
        goal={isCreateMode ? null : row}
        draft={isCreateMode ? draft : null}
        accounts={reference.accounts}
        onSubmit={handleSubmit}
        onCancel={isCreateMode ? onClose : () => setIsEditing(false)}
        saving={saving}
      />
    );
  } else if (!row) {
    body = loading ? (
      <Flex justify="center" py={12}><Spinner size="xl" /></Flex>
    ) : null;
  } else {
    const summary = row.summary;
    const statusKey = goalStatusKey(summary);
    const accountsById = new Map(reference.accounts.map((a) => [a.id, a]));
    const goalAccountIds = row.accounts.map((a) => a.account_id);
    const savedById = new Map((summary?.by_account || []).map((b) => [b.account_id, b.saved]));
    const nativeById = new Map((summary?.by_account || [])
      .filter((b) => b.native_currency)
      .map((b) => [b.account_id, {
        currency: b.native_currency, saved: b.native_saved, rateSources: b.rate_sources,
      }]));
    const sittingIds = [...goalAccountIds, ...[...savedById.keys()].filter((id) => !goalAccountIds.includes(id))];
    const monthsLeft = monthsUntil(row.target_date);
    const reasons = [...new Set((summary?.invalid || []).map((i) => i.reason))];
    const editSummary = canUpdate ? () => setIsEditing(true) : undefined;

    body = (
      <Box>
        {/* Title block: who the goal is on the left, how much is in it on the right */}
        <Flex align="flex-start" justify="space-between" gap={4} mb={5}>
          <Flex align="center" gap={3} minW={0}>
            <GoalIcon goal={row} size={44} />
            <Box minW={0}>
              <Text fontSize={{ base: 'lg', md: '2xl' }} fontWeight={600} color="redesign.heading" lineHeight={1.15} noOfLines={2}>
                {row.name}
              </Text>
              <Text fontSize="xs" color="redesign.text.muted" mt={1}>
                {statusKey && <Text as="span" color={STATUS_COLORS[statusKey]}>{t(`status.${statusKey}`)}</Text>}
                {row.saving_mode === 'target_date' && !summary?.is_achieved && !summary?.is_overdue && (
                  <> · {t(monthsLeft === 1 ? 'status.monthsLeftOne' : 'status.monthsLeftOther', { count: monthsLeft })}</>
                )}
                {row.target_date && <> · {t('status.target', { date: formatMonthYear(row.target_date) })}</>}
                {row.saving_mode === 'monthly_contribution' && (
                  <> · {t('status.monthly', { amount: `${row.currency} ${formatAmount(row.monthly_contribution)}` })}</>
                )}
              </Text>
            </Box>
          </Flex>
          {summary && (
            <Box textAlign="right" flexShrink={0}>
              <Text textStyle="microLabel" mb={1}>{t('detail.saved')}</Text>
              <Figure size="lg" code={summary.currency} amount={formatAmount(summary.saved)} display="block" />
              {summary.remaining > 0 && (
                <Text fontSize="xs" color="redesign.text.muted" mt={2}>
                  {t('detail.toGo', { amount: formatAmount(summary.remaining) })}
                </Text>
              )}
            </Box>
          )}
        </Flex>

        {summary && (
          <>
            {row.saving_mode === 'monthly_contribution' ? (
              <GoalMonthDots goal={row} summary={summary} />
            ) : (
              <>
                <GoalBar pct={summary.pct} color={row.color} h="4px" />
                <Text fontSize="xs" color="redesign.text.muted" mt={2}>
                  {t('summary.pctFunded', { pct: Math.round(summary.pct) })}
                </Text>
              </>
            )}
            <Insight goal={row} summary={summary} />
            {summary.approximate && (
              <Text fontSize="xs" color="redesign.text.muted" textAlign="center" mb={2}>
                {summary.num_unconvertible > 0
                  ? t('approximate', { excluded: summary.num_unconvertible })
                  : t('approximateConverted')}
              </Text>
            )}
          </>
        )}

        <Box mt={6}>
          <SectionTitle>{t('summary.heading')}</SectionTitle>
          <SummaryRow label={t('summary.goalAmount')} value={`${row.currency} ${formatAmount(row.target_amount)}`} onEdit={editSummary} />
          <SummaryRow label={t('summary.startDate')} value={formatMonthYear(row.start_date)} onEdit={editSummary} />
          {row.saving_mode === 'target_date'
            ? <SummaryRow label={t('summary.targetDate')} value={formatMonthYear(row.target_date)} onEdit={editSummary} />
            : <SummaryRow label={t('summary.monthlyContribution')} value={`${row.currency} ${formatAmount(row.monthly_contribution)}`} onEdit={editSummary} />}
          <SummaryRow label={t('summary.savingMode')} value={t(`modes.${row.saving_mode}`)} onEdit={editSummary} />
        </Box>

        <Box mt={8}>
          <SectionTitle
            action={canUpdate && !editingAccounts && (
              <Text
                as="button"
                type="button"
                fontSize="xs"
                fontWeight={500}
                color="redesign.accent.blue"
                onClick={() => { setAccountIds(goalAccountIds); setEditingAccounts(true); }}
              >
                {t('accounts.edit')}
              </Text>
            )}
          >
            {t('accounts.heading')}
          </SectionTitle>
          {editingAccounts ? (
            <Box mt={2}>
              <GoalAccountsPicker accounts={reference.accounts} selectedIds={accountIds} onChange={setAccountIds} />
              <Flex mt={3} gap={2}>
                <Button variant="primary" size="sm" flex={1} onClick={handleSaveAccounts} isLoading={saving} isDisabled={accountIds.length === 0}>
                  {t('buttons.save')}
                </Button>
                <Button variant="outline" size="sm" onClick={() => setEditingAccounts(false)}>{t('buttons.cancel')}</Button>
              </Flex>
            </Box>
          ) : (
            sittingIds.map((id) => {
              const account = accountsById.get(id);
              const name = account?.name || row.accounts.find((a) => a.account_id === id)?.name || `#${id}`;
              const saved = savedById.get(id) || 0;
              const native = nativeById.get(id);
              const digits = String(account?.source_id || '').slice(-4);
              return (
                <Flex key={id} align="center" justify="space-between" h="40px" gap={3}>
                  <Flex align="center" gap={3} minW={0}>
                    <Flex w="26px" h="26px" borderRadius="8px" bg={hexToTint(row.color)} color={row.color || 'redesign.accent.blue'} align="center" justify="center" fontSize="2xs" fontWeight={700} flexShrink={0}>
                      {name.charAt(0).toUpperCase()}
                    </Flex>
                    <Text fontSize="sm" color="redesign.heading" isTruncated>{name}</Text>
                    {digits && <Text fontSize="2xs" color="redesign.text.muted" flexShrink={0}>{digits}</Text>}
                  </Flex>
                  <RateSourceTooltip
                    sources={native?.rateSources}
                    isDisabled={!(native && native.currency !== summary?.currency)}
                  >
                  <Flex align="baseline" gap={2} flexShrink={0}>
                    <Text fontSize="sm" fontWeight={500} color={saved < 0 ? 'redesign.accent.red' : 'redesign.heading'}>
                      {formatAmount(saved)}
                    </Text>
                    {/* An account in another currency says what it holds in its
                        own money; the figure above is the goal's currency. */}
                    <Text fontSize="2xs" color="redesign.text.muted">
                      {native && native.currency !== summary?.currency
                        ? `≈ ${native.currency} ${formatAmount(native.saved)}`
                        : summary?.currency}
                    </Text>
                  </Flex>
                  </RateSourceTooltip>
                </Flex>
              );
            })
          )}
        </Box>

        <Box mt={8}>
          <SectionTitle
            action={canUpdate && (
              <Button size="xs" variant="ghost" leftIcon={<AddIcon boxSize={2} />} color="redesign.accent.blue" onClick={onAttachOpen}>
                {t('movements.attach')}
              </Button>
            )}
          >
            {t('movements.heading')}
          </SectionTitle>
          {reasons.length > 0 && (
            <Box my={2} px={3} py={2} borderRadius="lg" bg="rgba(180, 83, 9, 0.12)" _dark={{ bg: 'rgba(250, 204, 21, 0.14)' }}>
              <Flex align="center" gap={2}>
                <WarningIcon color="redesign.accent.amber" boxSize={3} />
                <Text fontSize="xs" color="redesign.heading" fontWeight={500}>
                  {t(summary.invalid.length === 1 ? 'movements.invalidOne' : 'movements.invalidOther', { count: summary.invalid.length })}
                </Text>
              </Flex>
              {reasons.map((reason) => (
                <Text key={reason} fontSize="xs" color="redesign.text.muted" ml={5}>{t(`reasons.${reason}`, { defaultValue: reason })}</Text>
              ))}
            </Box>
          )}
          <GoalMovementsList
            goalId={goalId}
            refreshKey={movementsRefreshKey}
            onSelect={setSelectedMovementId}
          />
        </Box>
      </Box>
    );
  }

  return (
    <Box h="100%" bg="redesign.bg.surface" display="flex" flexDirection="column" position="relative">
      {selectedMovementId && (
        <Box position="absolute" inset={0} bg="redesign.bg.surface" zIndex={2} display="flex" flexDirection="column">
          <MovementDetailPanel
            movementId={selectedMovementId}
            onClose={handleCloseOverlay}
            onMovementUpdated={() => setOverlayDirty(true)}
            onMovementDeleted={() => setOverlayDirty(true)}
            onSelectMovement={setSelectedMovementId}
          />
        </Box>
      )}
      {header}
      <Box flex="1" overflowY="auto" overflowX="hidden" px={{ base: 3, md: 6 }} py={{ base: 3, md: 5 }}>
        {body}
      </Box>
      {row && (
        <GoalAttachModal
          goal={row}
          isOpen={isAttachOpen}
          onClose={onAttachClose}
          onAttached={() => { setMovementsRefreshKey((k) => k + 1); onChanged(); }}
        />
      )}
    </Box>
  );
}
